import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../services/api';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const VITAL_TYPES = [
  { value: 'blood_pressure', label: 'Blood Pressure' },
  { value: 'blood_sugar', label: 'Blood Sugar' },
  { value: 'hba1c', label: 'HbA1c (Glycated Haemoglobin)' },
  { value: 'weight', label: 'Weight' },
  { value: 'heart_rate', label: 'Heart Rate' },
  { value: 'spo2', label: 'SpO2 (Oxygen Saturation)' },
  { value: 'temperature', label: 'Temperature' }
];

const READING_TYPES = [
  { value: 'fasting', label: 'Fasting' },
  { value: 'pp', label: 'PP (Post-Prandial)' },
  { value: 'random', label: 'Random' }
];

function VitalsForm({ user }) {
  const navigate = useNavigate();
  const { patientId } = useParams();

  const [formData, setFormData] = useState({
    vital_type: '',
    recorded_at: '',
    notes: '',
    // Blood pressure
    systolic: '',
    diastolic: '',
    // Blood sugar
    value: '',
    reading_type: '',
    // HbA1c
    hba1c_value: '',
    // Weight
    weight_value: '',
    // Heart rate
    hr_value: '',
    // SpO2
    spo2_value: '',
    // Temperature
    temp_value: '',
    temp_unit: 'celsius'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  useEffect(() => {
    // Set default recorded_at to current date and time
    const now = new Date();
    const localDateTime = new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
    setFormData(prev => ({ ...prev, recorded_at: localDateTime }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        patient_id: patientId,
        vital_type: formData.vital_type,
        recorded_at: formData.recorded_at,
        notes: formData.notes || ''
      };

      // Add type-specific fields
      if (formData.vital_type === 'blood_pressure') {
        if (!formData.systolic || !formData.diastolic) {
          setError('Systolic and Diastolic are required for Blood Pressure');
          setLoading(false);
          return;
        }
        payload.systolic = parseInt(formData.systolic);
        payload.diastolic = parseInt(formData.diastolic);
      } else if (formData.vital_type === 'blood_sugar') {
        if (!formData.sugar_value) {
          setError('Value is required for Blood Sugar');
          setLoading(false);
          return;
        }
        if (!formData.reading_type) {
          setError('Reading Type is required for Blood Sugar');
          setLoading(false);
          return;
        }
        payload.sugar_value = parseFloat(formData.sugar_value);
        payload.reading_type = formData.reading_type;
      } else if (formData.vital_type === 'hba1c') {
        if (!formData.sugar_value) {
          setError('HbA1c value is required');
          setLoading(false);
          return;
        }
        payload.vital_type = 'blood_sugar';
        payload.sugar_value = parseFloat(formData.sugar_value);
        payload.reading_type = 'hba1c';
      } else if (formData.vital_type === 'weight') {
        if (!formData.weight_value) {
          setError('Weight is required');
          setLoading(false);
          return;
        }
        payload.weight_value = parseFloat(formData.weight_value);
        if (formData.height_value) {
          payload.height_value = parseFloat(formData.height_value);
        }
      } else if (formData.vital_type === 'heart_rate') {
        if (!formData.hr_value) {
          setError('BPM is required for Heart Rate');
          setLoading(false);
          return;
        }
        payload.value = parseInt(formData.hr_value);
      } else if (formData.vital_type === 'spo2') {
        if (!formData.spo2_value) {
          setError('SpO2 percentage is required');
          setLoading(false);
          return;
        }
        payload.value = parseFloat(formData.spo2_value);
      } else if (formData.vital_type === 'temperature') {
        if (!formData.temp_value) {
          setError('Temperature value is required');
          setLoading(false);
          return;
        }
        payload.value = parseFloat(formData.temp_value);
        payload.unit = formData.temp_unit;
      }

      await api.post(`${API_URL}/vitals`, payload, { headers });

      setSuccess('Vital recorded successfully!');
      setTimeout(() => {
        navigate(`/patients/${patientId}`);
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record vital');
    } finally {
      setLoading(false);
    }
  };

  const renderDynamicFields = () => {
    switch (formData.vital_type) {
      case 'blood_pressure':
        return (
          <>
            <div className="form-group">
              <label htmlFor="systolic" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Systolic (mmHg) *</label>
              <input
                type="number"
                id="systolic"
                name="systolic"
                value={formData.systolic}
                onChange={handleChange}
                placeholder="e.g., 120"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="diastolic" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Diastolic (mmHg) *</label>
              <input
                type="number"
                id="diastolic"
                name="diastolic"
                value={formData.diastolic}
                onChange={handleChange}
                placeholder="e.g., 80"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                required
              />
            </div>
          </>
        );

      case 'blood_sugar':
        return (
          <>
            <div className="form-group">
              <label htmlFor="sugar_value" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Value (mg/dL) *</label>
              <input
                type="number"
                id="sugar_value"
                name="sugar_value"
                value={formData.sugar_value}
                onChange={handleChange}
                placeholder="e.g., 100"
                step="0.1"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="reading_type" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Reading Type *</label>
              <select
                id="reading_type"
                name="reading_type"
                value={formData.reading_type}
                onChange={handleChange}
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                required
              >
                <option value="">Select reading type</option>
                {READING_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
              </select>
            </div>
          </>
        );

      case 'hba1c':
        return (
          <div className="form-group">
            <label htmlFor="sugar_value" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>HbA1c (%) *</label>
            <input
              type="number"
              id="sugar_value"
              name="sugar_value"
              value={formData.sugar_value}
              onChange={handleChange}
              placeholder="e.g., 5.7"
              step="0.1"
              style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
              required
            />
            <small style={{ color: '#718096', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
              Glycated Haemoglobin (HbA1c) measures average blood glucose over the past 2-3 months
            </small>
          </div>
        );

      case 'weight':
        return (
          <div className="form-group">
            <label htmlFor="weight_value" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Weight (kg) *</label>
            <input
              type="number"
              id="weight_value"
              name="weight_value"
              value={formData.weight_value}
              onChange={handleChange}
              placeholder="e.g., 70"
              step="0.1"
              style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
              required
            />
            <small style={{ color: '#718096', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
              BMI will be calculated automatically using the patient's stored height (if available)
            </small>
          </div>
        );

      case 'heart_rate':
        return (
          <div className="form-group">
            <label htmlFor="hr_value" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Beats Per Minute (BPM) *</label>
            <input
              type="number"
              id="hr_value"
              name="hr_value"
              value={formData.hr_value}
              onChange={handleChange}
              placeholder="e.g., 72"
              style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
              required
            />
          </div>
        );

      case 'spo2':
        return (
          <div className="form-group">
            <label htmlFor="spo2_value" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>SpO2 Percentage (%) *</label>
            <input
              type="number"
              id="spo2_value"
              name="spo2_value"
              value={formData.spo2_value}
              onChange={handleChange}
              placeholder="e.g., 98"
              step="0.1"
              min="0"
              max="100"
              style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
              required
            />
          </div>
        );

      case 'temperature':
        return (
          <>
            <div className="form-group">
              <label htmlFor="temp_value" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Temperature Value *</label>
              <input
                type="number"
                id="temp_value"
                name="temp_value"
                value={formData.temp_value}
                onChange={handleChange}
                placeholder="e.g., 36.5"
                step="0.1"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="temp_unit" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Unit</label>
              <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="temp_unit"
                    value="celsius"
                    checked={formData.temp_unit === 'celsius'}
                    onChange={handleChange}
                    style={{ marginRight: '8px' }}
                  />
                  °C (Celsius)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="temp_unit"
                    value="fahrenheit"
                    checked={formData.temp_unit === 'fahrenheit'}
                    onChange={handleChange}
                    style={{ marginRight: '8px' }}
                  />
                  °F (Fahrenheit)
                </label>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  if (!patientId) {
    return (
      <div className="dashboard">
        <Navbar user={user} onLogout={handleLogout} />
        <div className="dashboard-content">
          <div className="api-error">No patient ID provided</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Navbar user={user} onLogout={handleLogout} />

      <div className="dashboard-content">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2>Add Vital Sign</h2>
          <Link
            to={`/patients/${patientId}`}
            style={{ color: '#2b6cb0', textDecoration: 'none' }}
          >
            ← Back to Patient
          </Link>
        </div>

        {error && <div className="api-error" style={{ marginBottom: '20px' }}>{error}</div>}
        {success && <div className="success-message" style={{ backgroundColor: '#c6f6d5', color: '#22543d', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>{success}</div>}

        <div style={{ background: 'white', padding: '32px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: '600px', margin: '0 auto' }}>
          <form onSubmit={handleSubmit}>
            <div className="form-row" style={{ marginBottom: '20px' }}>
              <div className="form-group">
                <label htmlFor="vital_type" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Vital Type *</label>
                <select
                  id="vital_type"
                  name="vital_type"
                  value={formData.vital_type}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                  required
                >
                  <option value="">Select vital type</option>
                  {VITAL_TYPES.map(vt => <option key={vt.value} value={vt.value}>{vt.label}</option>)}
                </select>
              </div>
            </div>

            {formData.vital_type && renderDynamicFields()}

            <div className="form-row" style={{ marginTop: '20px' }}>
              <div className="form-group">
                <label htmlFor="recorded_at" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Date & Time</label>
                <input
                  type="datetime-local"
                  id="recorded_at"
                  name="recorded_at"
                  value={formData.recorded_at}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '20px' }}>
              <label htmlFor="notes" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Notes (optional)</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any additional notes..."
                rows="3"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem', resize: 'vertical' }}
              />
            </div>

            <div className="form-actions" style={{ display: 'flex', gap: '12px', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
              <button
                type="submit"
                className="submit-btn"
                disabled={loading}
                style={{ padding: '12px 32px' }}
              >
                {loading ? 'Saving...' : 'Save Vital'}
              </button>
              <Link
                to={`/patients/${patientId}`}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#cbd5e0',
                  color: '#4a5568',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default VitalsForm;
